// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useEffect, useMemo, useState } from 'react';
import { Image, StyleSheet, useWindowDimensions, View } from 'react-native';
import { Redirect, useLocalSearchParams, useRootNavigationState } from 'expo-router';

import { Text } from '@/components/Themed';
import { useUser } from './user-context';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { useI18n } from '@/i18n';
import { AuthStatusCard } from '@/components/AuthStatusCard';
import { useAuthRedirect } from '@/hooks/useAuthRedirect';
import LoginScreen from './login.screen';
import { getAssetImageSource } from '@/services/assetImageSource';
import appColors from '@/theme/colors';

const MOBILE_BREAKPOINT = 820;

export default function LoginWebScreen() {
  const { width } = useWindowDimensions();
  const { user, setUser } = useUser();
  const navState = useRootNavigationState();
  const isNavigationReady = !!navState?.key;
  const { debug: debugParam, reason } = useLocalSearchParams<{ debug?: string; reason?: string }>();
  const { t } = useI18n();

  const [error, setError] = useState<string | null>(null);
  const debug = useMemo(() => debugParam === 'true', [debugParam]);
  const loginHeroImage = useMemo(() => getAssetImageSource('images/login-hero.png', 'hero', '/login'), []);
  const { loading, handleRedirect } = useAuthRedirect({ user, setUser, onError: setError, debug });

  useEffect(() => {
    if (!isNavigationReady) return;
    if (user) return;
    if (reason === 'user-not-found') {
      setError(t('login.userNotFound'));
    }
  }, [user, isNavigationReady, reason, t]);

  if (width < MOBILE_BREAKPOINT) {
    return <LoginScreen />;
  }

  return (
    <>
      {isNavigationReady && user ? <Redirect href={`/tournaments${debug ? '?debug=true' : ''}`} /> : null}
      <View style={styles.shell}>
        <View style={styles.frame}>
          <View style={styles.heroPanel}>
            <Text style={styles.kicker}>{t('auth.loginSubtitle')}</Text>
            <Text style={styles.title}>{t('auth.loginTitle')}</Text>
            <Text style={styles.tagline}>{t('auth.loginHint')}</Text>
            <Image source={loginHeroImage} style={styles.heroImage} resizeMode="cover" />
          </View>
          <View style={styles.cardPanel}>
            <View style={styles.languageRow}>
              <LanguageSwitcher />
            </View>
            <AuthStatusCard
              title={t('auth.loginCardTitle')}
              error={error}
              buttonLabel={loading ? t('auth.loggingIn') : t('auth.loginCta')}
              onPress={handleRedirect}
              disabled={loading}
            >
              {debug ? <Text style={styles.metaLabel}>{t('auth.debugLabel')}</Text> : null}
              {debug ? (
                <Text style={styles.metaValue}>
                  {user ? `${user.forename} (${user.id})` : t('auth.debugNone')}
                </Text>
              ) : null}
              <Text style={styles.metaLabel}>{t('auth.loginSubtitle')}</Text>
              <Text style={styles.metaLabel}>{t('auth.loginHint')}</Text>
            </AuthStatusCard>
          </View>
        </View>
      </View>
    </>
  );
}

const styles = StyleSheet.create({
  shell: {
    flex: 1,
    backgroundColor: '#011806',
    padding: 32,
  },
  frame: {
    flex: 1,
    flexDirection: 'row',
    gap: 36,
    borderRadius: 26,
    backgroundColor: '#07200D',
    padding: 32,
    borderWidth: 1,
    borderColor: '#1A3A20',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  heroPanel: {
    flex: 1,
    gap: 16,
  },
  kicker: {
    fontSize: 14,
    letterSpacing: 2,
    textTransform: 'uppercase',
    color: '#A1D493',
    fontWeight: '700',
  },
  title: {
    fontSize: 40,
    fontWeight: '800',
    color: appColors.text.primary,
  },
  tagline: {
    fontSize: 16,
    color: appColors.text.primary,
    maxWidth: 380,
  },
  heroImage: {
    width: 340,
    height: 340,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#1A3A20',
  },
  cardPanel: {
    width: 420,
    gap: 16,
  },
  languageRow: {
    alignItems: 'flex-end',
  },
  metaLabel: {
    fontSize: 14,
    fontWeight: '700',
    color: appColors.text.primary,
  },
  metaValue: {
    fontSize: 16,
    fontWeight: '600',
    color: appColors.text.primary,
    marginBottom: 4,
  },
});
